/**
 * Google Maps wrapper using @vis.gl/react-google-maps
 * Uses useMap hook to imperatively control the map center
 */
import React, { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { Map, Marker, useMap } from '@vis.gl/react-google-maps';
import { tierColor, type Tier } from '../theme/tokens';

// Custom map style - warm, muted palette that makes markers pop
const MAP_STYLES: google.maps.MapTypeStyle[] = [
  // Base: warm off-white/cream background
  { elementType: 'geometry', stylers: [{ color: '#F5F1EB' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5C5750' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#F5F1EB' }] },
  
  // Water: soft sage/blue-gray
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#C5D5D8' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#8A9A9D' }] },
  
  // Land/parks: muted sage green
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#D5DFD0' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#7A7A70' }] },
  
  // Roads: subtle warm grays
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#FFFFFF' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#E5E0D8' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#F0EBE3' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#D5D0C8' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#7A7570' }] },
  
  // Transit: muted
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#E8E3DB' }] },
  { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#7A7570' }] },
  
  // Buildings: very subtle
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  
  // Administrative: subtle borders
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#D5D0C8' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
];

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  tier: Tier;
}

export interface ViewportBounds {
  center: { lat: number; lng: number };
  zoom: number;
  bbox: [number, number, number, number]; // [south, west, north, east]
}

interface MapCanvasProps {
  center: { lat: number; lng: number };
  zoom: number;
  markers: MapMarker[];
  searchLocation?: { lat: number; lng: number } | null;
  onViewportChange?: (v: ViewportBounds) => void;
  onSearchThisArea?: (center: { lat: number; lng: number }, zoom: number) => void;
  onMarkerClick?: (id: string) => void;
  highlightedMarkerId?: string | null;
  className?: string;
}

// Create SVG marker icon as data URL (circle for parkings)
const createMarkerIcon = (color: string, isHighlighted: boolean): string => {
  const size = isHighlighted ? 28 : 20;
  const strokeWidth = isHighlighted ? 3 : 2;
  const stroke = isHighlighted ? '#000' : '#fff';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${size/2}" cy="${size/2}" r="${size/2 - strokeWidth}" fill="${color}" stroke="${stroke}" stroke-width="${strokeWidth}"/></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Create search location marker (pin/drop shape in blue)
const createSearchLocationIcon = (): string => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
    <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24C32 7.163 24.837 0 16 0z" fill="#3B82F6" stroke="#1E40AF" stroke-width="2"/>
    <circle cx="16" cy="14" r="6" fill="white"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Calculate distance between two points in km
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Inner component that uses the map hook
const MapContent: React.FC<{
  center: { lat: number; lng: number };
  zoom: number;
  markers: MapMarker[];
  searchLocation?: { lat: number; lng: number } | null;
  onMarkerClick?: (id: string) => void;
  highlightedMarkerId?: string | null;
  onMapMoved?: (newCenter: { lat: number; lng: number }, zoom: number) => void;
}> = ({ center, zoom, markers, searchLocation, onMarkerClick, highlightedMarkerId, onMapMoved }) => {
  const map = useMap();
  const isInitialMount = useRef(true);

  // Imperatively set center when it changes or map becomes available
  useEffect(() => {
    if (!map) return;
    
    map.setCenter({ lat: center.lat, lng: center.lng });
    map.setZoom(zoom);
  }, [map, center.lat, center.lng, zoom]);

  // Listen for map idle events (after pan/zoom completes)
  useEffect(() => {
    if (!map) return;

    const handleIdle = () => {
      // Skip the initial mount
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }

      const mapCenter = map.getCenter();
      const mapZoom = map.getZoom();
      if (mapCenter && mapZoom !== undefined) {
        onMapMoved?.({ lat: mapCenter.lat(), lng: mapCenter.lng() }, mapZoom);
      }
    };

    const listener = map.addListener('idle', handleIdle);
    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [map, onMapMoved]);

  const handleMarkerClick = useCallback((id: string) => {
    onMarkerClick?.(id);
  }, [onMarkerClick]);

  // Memoize parking markers
  const markerElements = useMemo(() => {
    return markers.map((m) => {
      const isHighlighted = m.id === highlightedMarkerId;
      const colors = tierColor(m.tier as Tier);
      const markerColor = colors?.marker ?? '#6B7280'; // Use marker color, not bg
      
      return (
        <Marker
          key={m.id}
          position={{ lat: m.lat, lng: m.lng }}
          onClick={() => handleMarkerClick(m.id)}
          zIndex={isHighlighted ? 1000 : 1}
          icon={{
            url: createMarkerIcon(markerColor, isHighlighted),
            scaledSize: { width: isHighlighted ? 28 : 20, height: isHighlighted ? 28 : 20 } as google.maps.Size,
            anchor: { x: isHighlighted ? 14 : 10, y: isHighlighted ? 14 : 10 } as google.maps.Point,
          }}
        />
      );
    });
  }, [markers, highlightedMarkerId, handleMarkerClick]);

  // Search location marker
  const searchMarker = useMemo(() => {
    if (!searchLocation) return null;
    return (
      <Marker
        key="search-location"
        position={{ lat: searchLocation.lat, lng: searchLocation.lng }}
        zIndex={2000}
        icon={{
          url: createSearchLocationIcon(),
          scaledSize: { width: 32, height: 40 } as google.maps.Size,
          anchor: { x: 16, y: 40 } as google.maps.Point,
        }}
        title="Search location"
      />
    );
  }, [searchLocation]);

  return (
    <>
      {searchMarker}
      {markerElements}
    </>
  );
};

export const MapCanvas: React.FC<MapCanvasProps> = ({
  center,
  zoom,
  markers,
  searchLocation,
  onSearchThisArea,
  onMarkerClick,
  highlightedMarkerId,
  className = '',
}) => {
  // Track if user has moved the map far enough from search location
  const [showSearchButton, setShowSearchButton] = useState(false);
  const [mapCenter, setMapCenter] = useState(center);
  const [currentZoom, setCurrentZoom] = useState(zoom);
  
  // Unique key based on center AND zoom - forces complete remount when search location changes
  const mapKey = `map-${center.lat.toFixed(4)}-${center.lng.toFixed(4)}-${zoom}`;

  // Reset button visibility when center prop changes (new search)
  useEffect(() => {
    setShowSearchButton(false);
    setMapCenter(center);
  }, [center.lat, center.lng]);

  // Handle map movement
  const handleMapMoved = useCallback((newCenter: { lat: number; lng: number }, newZoom: number) => {
    setMapCenter(newCenter);
    setCurrentZoom(newZoom);
    
    // Show button if moved more than 500m from original search location
    if (searchLocation) {
      const distance = haversineKm(
        searchLocation.lat, searchLocation.lng,
        newCenter.lat, newCenter.lng
      );
      setShowSearchButton(distance > 0.5); // 500 meters threshold
    }
  }, [searchLocation]);

  // Handle search this area click
  const handleSearchThisArea = useCallback(() => {
    setShowSearchButton(false);
    onSearchThisArea?.(mapCenter, currentZoom);
  }, [mapCenter, currentZoom, onSearchThisArea]);

  return (
    <div 
      className={className} 
      style={{ 
        width: '100%', 
        height: '100%',
        maxHeight: '100vh',
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      <Map
        key={mapKey}
        defaultCenter={center}
        defaultZoom={zoom}
        gestureHandling="greedy"
        disableDefaultUI
        reuseMaps={false}
        styles={MAP_STYLES}
        style={{ width: '100%', height: '100%' }}
      >
        <MapContent
          center={center}
          zoom={zoom}
          markers={markers}
          searchLocation={searchLocation}
          onMarkerClick={onMarkerClick}
          highlightedMarkerId={highlightedMarkerId}
          onMapMoved={handleMapMoved}
        />
      </Map>

      {/* Search this area button */}
      {showSearchButton && onSearchThisArea && (
        <button
          onClick={handleSearchThisArea}
          className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-4 py-2.5 bg-white rounded-full shadow-lg border border-gray-200 text-[14px] font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          Search this area
        </button>
      )}
    </div>
  );
};
